import {
  ButtonType,
  CaesarsButton,
} from '@eCommerce/frontend-legacy-shared-library-native';
import {useSelector} from 'Provider';
import {HStack, Text, VStack, View} from 'native-base';
import React, {useState} from 'react';
import Icon from 'react-native-vector-icons/FontAwesome5Pro';

import ConnectedOfferFilterDestination from 'containers/OfferFilterDestination';
import ConnectedOfferFilterProperties from 'containers/OfferFilterProperties';
import {Markets} from 'models/Market';
import {OfferType} from 'models/Offer';
import {
  OfferFiltersDestination,
  OfferFiltersProperty,
} from 'models/OfferFilters';
import {AvailableFilters} from 'store/offers/offers.reducer';
import {
  offersFilteredSelector,
  shouldApplyFiltersSelector,
} from 'store/offers/offers.selectors';
import {Properties} from 'store/properties/properties.interface';
import {createDestinations} from 'utils/createDestinations';

import OfferFilterDate from './FilterDates';
import OfferTypesFilter from './OfferTypesFilter';
import {useTheme} from 'styled-components/native';

const FiltersDivider = () => {
  const theme = useTheme();
  const {athens} = theme.colors;

  return <View backgroundColor={athens} height="2px" width="100%" />;
};

interface FiltersTitleProps {
  title: string;
}

const FiltersTitle: React.FC<FiltersTitleProps> = ({title}) => {
  const theme = useTheme();
  const {paleSky, tuna} = theme.colors;

  return (
    <View paddingX="16px" paddingY="32px">
      <HStack alignItems="flex-start" alignContent="center">
        <Icon
          name="sliders-h"
          size={25}
          color={paleSky}
          style={{
            marginRight: theme.space[4],
          }}
          solid
        />
        <Text fontSize={5} fontFamily="heading" fontWeight={700} color={tuna}>
          {title.toUpperCase()}
        </Text>
      </HStack>
    </View>
  );
};

export interface OfferFiltersProps {
  markets: Markets;
  properties: Properties;
  setSelectedDestination: (
    selectedDestination?: OfferFiltersDestination
  ) => void;
  setSelectedProperties: (selectedProperties?: OfferFiltersProperty[]) => void;
  selectedOfferTypes: OfferType[];
  setSelectedOfferTypes: (offerTypes: OfferType[]) => void;
  availableFilters: AvailableFilters;
  clearFilters: () => void;
  selectedProperties: OfferFiltersProperty[] | undefined;
  selectedDestination: OfferFiltersDestination | undefined;
}

const OfferFilters: React.FC<OfferFiltersProps> = props => {
  const {
    markets,
    properties,
    setSelectedDestination,
    setSelectedProperties,
    selectedOfferTypes,
    setSelectedOfferTypes,
    availableFilters,
    clearFilters,
    selectedProperties,
    selectedDestination,
  } = props;
  const list: OfferFiltersDestination[] = createDestinations(
    markets,
    properties
  );

  const [destinationToggle, setDestinationToggle] = useState(true);
  const [propertiesToggle, setPropertiesToggle] = useState(false);
  const [dateToggle, setDateToggle] = useState(false);
  const filteredOffers = useSelector(offersFilteredSelector);
  const hasFiltersApplied = useSelector(shouldApplyFiltersSelector);

  const availableDestinationProperties = selectedDestination?.properties.filter(
    property => availableFilters.properties?.includes(property.id.toUpperCase())
  );

  const handleSelectDestination = (
    item: OfferFiltersDestination | undefined
  ) => {
    setSelectedDestination(item);
    setDestinationToggle(!item);
    item && setPropertiesToggle(item.properties.length > 1);
  };

  const handleDestinationToggle = () => setDestinationToggle(prev => !prev);

  // DRY: Same logic exists in screens/Offers/HotelsAndResortsMobile
  const handleSelectProperty = (item: OfferFiltersProperty) => {
    if (selectedProperties) {
      const position = selectedProperties.indexOf(item);
      // If item exists, delete it from the list
      if (position !== -1) {
        const newProperties = [...selectedProperties];
        newProperties.splice(position, 1);
        setSelectedProperties(newProperties);
      } else {
        // If we've selected all the properties, it's the same as having none selected
        if (
          selectedProperties.length + 1 ===
          availableDestinationProperties?.length
        ) {
          setSelectedProperties([]);
        } else {
          // Add to the list
          setSelectedProperties([...selectedProperties, item]);
        }
      }
    }
  };

  const handlePropertiesToggle = () => setPropertiesToggle(prev => !prev);

  const handleDateToggle = () => {
    setDateToggle(prev => !prev);
  };

  const handleSelectAllProperties = () => setSelectedProperties([]);

  const handleClearFiltersPressed = () => {
    clearFilters && clearFilters();
  };

  const filteredTitleText = () => {
    if (hasFiltersApplied) {
      if (filteredOffers.results.length === 1) {
        return '1 RESULT';
      }
      return `${filteredOffers.results.length} RESULTS`;
    }
    return 'FILTER';
  };

  return (
    <View shadow={3}>
      <FiltersTitle title={filteredTitleText()} />
      <FiltersDivider />
      <ConnectedOfferFilterDestination
        list={list}
        selected={selectedDestination}
        onSelect={handleSelectDestination}
        toggle={destinationToggle}
        onToggle={handleDestinationToggle}
        availableProperties={availableFilters.properties}
      />
      {selectedDestination && (
        <>
          <FiltersDivider />
          <ConnectedOfferFilterProperties
            list={selectedDestination ? selectedDestination.properties : []}
            selected={selectedProperties}
            onSelect={handleSelectProperty}
            onSelectAll={handleSelectAllProperties}
            toggle={propertiesToggle}
            onToggle={handlePropertiesToggle}
            availableProperties={availableFilters.properties}
          />
        </>
      )}
      <FiltersDivider />
      <OfferFilterDate toggle={dateToggle} onToggle={handleDateToggle} />
      <FiltersDivider />
      <OfferTypesFilter
        selectedOfferTypes={selectedOfferTypes}
        setSelectedOfferTypes={setSelectedOfferTypes}
        availableOfferTypes={availableFilters.types}
      />
      <FiltersDivider />
      <VStack p="16px" width="100%" alignItems="center" justifyContent="center">
        <CaesarsButton
          title="Clear Filters"
          type={ButtonType.SECONDARY}
          testID="clear-filters-button"
          onPress={handleClearFiltersPressed}
          isFullWidth
        />
      </VStack>
    </View>
  );
};

export default OfferFilters;
